/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  faPlus,
  faFolderOpen,
  faQrcode
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Modal } from 'react-bootstrap';
import {
  useForm,
  Controller,
  FieldValues,
  SubmitHandler
} from 'react-hook-form';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import Select from 'react-select';

import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import returnService from 'service/return';
import NewReturningDataTable from './NewReturningDataTable';
import providerEntityService from 'service/providerEntity';

const CreateNewReturning = () => {
  const { t, i18n } = useTranslation();
  const legalEntityType = useSelector(
    (state: any) => state?.auth?.legal_entity?.legal_entity_type
  );

  const {
    control,
    handleSubmit,
    setValue,
    register,
    formState: { errors }
  } = useForm();
  const dispatch: Dispatch<any> = useDispatch();
  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);
  const navigate = useNavigate();

  const [legalEntityOption, setLegalEntityOption] = useState<any>([]);
  const [openFolder, setOpenFolder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currencyOption, setCurrencyOption] = useState<any>([]);

  const CustomOption = ({ children, ...props }: any) => {
    return (
      <components.Option {...props}>
        {props.data.is_custom === false && (
          <FontAwesomeIcon icon={faQrcode} className="fs-9 me-2" />
        )}
        {children}
      </components.Option>
    );
  };

  //GET LEgal Entity to options
  useQuery(['GET_LEGAL_ENTITY'], async () => {
    await providerEntityService
      .getList({ offset: 0, limit: 100, legal_entity_type: legalEntityType })
      .then((res: any) => {
        const options = res?.legal_entities.map((option: any) => ({
          value: option.id,
          pid: option?.pid || 0,
          currency_pid: option?.currency?.pid || 0,
          address: option?.address || '',
          l_lat: option?.l_lat || 0,
          l_long: option?.l_long || 0,
          label: option.name,
          currencies: option?.currencies || [],
          is_custom: option.is_custom || false
        }));

        setLegalEntityOption(options);
      });
  });

  const onSubmit: SubmitHandler<FieldValues> = data => {
    if (!cashboxId) {
      dispatch(showAlert({ title: t('shift_not_open') }));
    }

    if (!data?.currency) {
      dispatch(
        showAlert({ title: `${t('currency')}} ${t('required_field')}` })
      );
      return;
    }

    const selectedRecipent = legalEntityOption.find(
      (item: any) => item?.value === data?.sender?.value
    );

    if (data?.sender?.value !== '' && cashboxId) {
      setLoading(true);
      returnService
        .createReturn({
          recipient_id: data?.sender?.value,
          recipient_pid: selectedRecipent?.pid || 0,
          cashbox_id: cashboxId,
          cashbox_pid: cashboxPid,
          currency_pid: +data?.currency || 0,
          address: selectedRecipent?.address || '',
          lat: selectedRecipent?.l_lat || 0,
          long: selectedRecipent?.l_long || 0,
          is_custom: selectedRecipent?.is_custom
        })
        .then((res: any) => {
          if (res) {
            setLoading(false);
            dispatch(
              showAlert({
                title: `${t('return')} ${t('successfully_added')}`,
                type: 'success'
              })
            );
            navigate(
              `/returning/outgoing/filter/newreturning/${res?.id}?sender_id=${res?.recipient_id}`,
              {
                state: {
                  newreturning_pid: res?.pid || 0,
                  recipient_pid: res?.recipient_pid || 0,
                  currency_pid: +data?.currency
                }
              }
            );
          }
        });
    }
  };

  return (
    <Form
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Form.Group className="md-4 mb-2">
        <Form.Label>{t('recipient')}</Form.Label>
        <div className="react-select-container">
          <Controller
            name="sender"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                closeMenuOnSelect={true}
                components={{ Option: CustomOption }}
                classNamePrefix="react-select"
                placeholder={t('recipient')}
                options={legalEntityOption}
                onChange={(e: any) => {
                  const selectedOption = legalEntityOption.find(
                    (option: any) => Number(option?.pid) === Number(e?.pid)
                  );

                  console.log('legalEntityOption', selectedOption);

                  if (selectedOption) {
                    const options = selectedOption?.currencies?.map(
                      (option: any) => ({
                        value: option?.pid || 0,
                        label: option?.name?.[i18n?.language]
                      })
                    );
                    setCurrencyOption(options);
                    setValue('address', selectedOption?.address);

                    const mainCurrency = selectedOption?.currencies?.find(
                      (currency: any) => currency?.is_main === true
                    );
                    setValue(
                      'currency',
                      mainCurrency?.pid || selectedOption?.currency_pid
                    );
                  }
                  field.onChange(e);
                }}
                styles={{
                  control: (base: any) => ({
                    ...base,
                    fontSize: '12.8px'
                  }),
                  singleValue: (base: any) => ({
                    ...base,
                    fontSize: '12.8px'
                  }),
                  menuPortal: (base: any) => ({
                    ...base,
                    fontSize: '12.8px',
                    color: 'red'
                  })
                }}
              />
            )}
          />
          {errors?.sender && (
            <span
              style={{
                marginTop: '2px',
                color: 'red',
                fontSize: '12px'
              }}
            >
              {t('required_field')}
            </span>
          )}
        </div>
      </Form.Group>
      <Form.Group className="md-4 mb-2">
        <div className="react-select-container">
          <Controller
            name="currency"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field }) => (
              <Form.Floating>
                <Form.Select {...field} isInvalid={!!errors.currency}>
                  <option className="d-none" value=""></option>
                  {currencyOption?.map((option: any) => (
                    <option
                      key={option.value}
                      value={option.value}
                      className="option"
                    >
                      {option.label}
                    </option>
                  ))}
                </Form.Select>

                <label htmlFor="floatingInputCustom">{t('currency')}</label>
                {errors?.currency && (
                  <span
                    style={{
                      marginTop: '2px',
                      color: 'red',
                      fontSize: '12px'
                    }}
                  >
                    {t('required_field')}
                  </span>
                )}
              </Form.Floating>
            )}
          />
        </div>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Floating className="md-4">
          <Form.Control
            disabled
            {...register('address')}
            type="text"
            placeholder="address"
            onFocus={e => e.target.select()}
          />
          <label htmlFor="floatingInputCustom">{t('address')}</label>
        </Form.Floating>
      </Form.Group>
      <Form.Group style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
        <Button
          variant="outline-primary"
          type="button"
          style={{ height: '48px', width: '100%' }}
          onClick={() => setOpenFolder(true)}
        >
          <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
          {t('open')}
        </Button>
        <Button
          variant="primary"
          type="submit"
          style={{ height: '48px', width: '100%' }}
          loading={loading}
          loadingPosition="start"
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          {t('create')}
        </Button>
      </Form.Group>

      <Modal
        show={openFolder}
        onHide={() => setOpenFolder(false)}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t('select_return')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewReturningDataTable setOpenFolder={setOpenFolder} />
        </Modal.Body>
      </Modal>
    </Form>
  );
};

export default CreateNewReturning;
